import { collection, addDoc, serverTimestamp, doc, getDoc, query, where, orderBy, limit, startAfter, endBefore, getDocs, QueryDocumentSnapshot, Timestamp, onSnapshot, getDocsFromCache, getDocsFromServer, Unsubscribe, Query, QueryConstraint } from 'firebase/firestore';
import { db, getUserId } from './firebase';
import { SalesTransaction, ItemType, Currency } from '../types'; // Added ItemType, Currency for better typing if needed for transactionToAdd
import { withAuthentication } from './authService';
import { toDateSafe } from '../utils/dateUtils';

// Collection reference
const getTransactionsCollection = () => {
  if (!db) throw new Error('Firestore yüklenemedi');
  return collection(db, 'transactions');
};

/**
 * Creates a new sales transaction record in Firestore.
 */
const _createSalesTransaction = async (
  transactionData: Omit<SalesTransaction, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<SalesTransaction> => {
  try {
    const userId = getUserId();
    console.log('_createSalesTransaction: User ID:', userId);
    
    // Explicitly construct the object to be sent to Firestore
    // to ensure no 'undefined' values are included for optional fields.
    const transactionToAdd: {
      userId: string;
      createdAt: any; // Firebase ServerTimestamp
      updatedAt: any; // Firebase ServerTimestamp
      itemId: string;
      itemName: string;
      itemBarcode: string;
      saleDate: Date;
      sellingPrice: number;
      sellingCurrency: Currency;
      costPrice: number;
      costCurrency: Currency;
      itemCategory?: string;
      itemType?: ItemType;
      sellingExchangeRateToTry?: number;
      costExchangeRateToTry?: number;
    } = {
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Required fields from transactionData
      itemId: transactionData.itemId,
      itemName: transactionData.itemName,
      itemBarcode: transactionData.itemBarcode,
      saleDate: transactionData.saleDate,
      sellingPrice: transactionData.sellingPrice,
      sellingCurrency: transactionData.sellingCurrency,
      costPrice: transactionData.costPrice,
      costCurrency: transactionData.costCurrency,
    };

    // Conditionally add optional fields if they are defined in transactionData
    if (transactionData.itemCategory !== undefined) {
      transactionToAdd.itemCategory = transactionData.itemCategory;
    }
    if (transactionData.itemType !== undefined) {
      transactionToAdd.itemType = transactionData.itemType;
    }
    if (transactionData.sellingExchangeRateToTry !== undefined) {
      transactionToAdd.sellingExchangeRateToTry = transactionData.sellingExchangeRateToTry;
    }
    if (transactionData.costExchangeRateToTry !== undefined) {
      transactionToAdd.costExchangeRateToTry = transactionData.costExchangeRateToTry;
    }

    console.log('_createSalesTransaction: Transaction to add to Firestore:', transactionToAdd);
    const docRef = await addDoc(getTransactionsCollection(), transactionToAdd);
    console.log('_createSalesTransaction: Successfully added transaction with ID:', docRef.id);
    
    // To return the full SalesTransaction object with resolved timestamps and ID
    if (!db) throw new Error('Firestore yüklenemedi');
    const newTransactionSnap = await getDoc(doc(db, 'transactions', docRef.id));
    if (!newTransactionSnap.exists()) {
        throw new Error('Failed to retrieve newly created transaction.');
    }
    const newTransaction = newTransactionSnap.data();
    
    return {
      id: newTransactionSnap.id,
      ...newTransaction,
      saleDate: toDateSafe(newTransaction.saleDate), // Ensure Dates are JS Dates
      createdAt: toDateSafe(newTransaction.createdAt),
      updatedAt: toDateSafe(newTransaction.updatedAt),
    } as SalesTransaction;

  } catch (error) {
    console.error('Error creating sales transaction:', error);
    console.error('_createSalesTransaction: Full error object during Firestore write:', error); // More detailed error logging
    throw error;
  }
};

/**
 * Fetches all sales transactions for the current user with pagination.
 */
const _getAllTransactions = async (
  pageSize: number = 15,
  cursor?: QueryDocumentSnapshot,
  direction: 'next' | 'prev' = 'next'
): Promise<{
  transactions: SalesTransaction[];
  hasNextPage: boolean; // Indicates if there MIGHT be a next page
  hasPrevPage: boolean; // Indicates if there MIGHT be a prev page
  firstVisibleDocument?: QueryDocumentSnapshot;
  lastVisibleDocument?: QueryDocumentSnapshot;
}> => {
  try {
    const userId = getUserId();
    let transactionsQuery;
    const queryConstraints: QueryConstraint[] = [
      where('userId', '==', userId),
      orderBy('saleDate', 'desc'), // Most recent sales first
    ];

    if (cursor) {
      if (direction === 'next') {
        // Add startAfter as a separate constraint
        transactionsQuery = query(
          getTransactionsCollection(), 
          where('userId', '==', userId),
          orderBy('saleDate', 'desc'),
          startAfter(cursor),
          limit(pageSize)
        );
      } else {
        // For 'prev' direction
        transactionsQuery = query(
          getTransactionsCollection(),
          where('userId', '==', userId),
          orderBy('saleDate', 'asc'), // Change to ascending
          endBefore(cursor),
          limit(pageSize)
        );
      }
    } else {
      // If no cursor, add the limit constraint separately
      transactionsQuery = query(
        getTransactionsCollection(),
        where('userId', '==', userId),
        orderBy('saleDate', 'desc'),
        limit(pageSize)
      );
    }

    // Try to get from cache first for better performance
    let snapshot;
    try {
      snapshot = await getDocsFromCache(transactionsQuery);
      
      if (snapshot.empty && snapshot.metadata.fromCache) {
        snapshot = await getDocsFromServer(transactionsQuery);
      } else if (snapshot.metadata.fromCache && snapshot.docs.length < pageSize && !cursor) {
        // Cache returned fewer items than requested for initial query - likely insufficient cache
        snapshot = await getDocsFromServer(transactionsQuery);
      }
    } catch (cacheError) {
      snapshot = await getDocsFromServer(transactionsQuery);
    }

    let fetchedTransactions = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        saleDate: toDateSafe(data.saleDate),
        createdAt: toDateSafe(data.createdAt),
        updatedAt: toDateSafe(data.updatedAt),
      } as SalesTransaction;
    });

    if (direction === 'prev' && cursor) {
        fetchedTransactions.reverse(); // Reverse to maintain 'desc' order for display
    }

    const firstDoc = snapshot.docs.length > 0 ? snapshot.docs[0] : undefined;
    const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined;
    
    // hasNextPage and hasPrevPage are estimations here
    // For true 'hasNextPage', you could fetch pageSize + 1 items
    const hasNextPage = fetchedTransactions.length === pageSize;
    const hasPrevPage = !!cursor; // Simplistic: if there was a cursor, there was a previous state

    const result = {
        transactions: fetchedTransactions,
        hasNextPage,
        hasPrevPage,
        firstVisibleDocument: direction === 'prev' && cursor ? lastDoc : firstDoc, // Adjust based on reversed query
        lastVisibleDocument: direction === 'prev' && cursor ? firstDoc : lastDoc
    };

    return result;

  } catch (error) {
    console.error('Error fetching sales transactions:', error);
    throw error;
  }
};

/**
 * Fetches all sales transactions for the current user within a given date range.
 */
const _getTransactionsByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<SalesTransaction[]> => {
  if (!db) throw new Error('Firestore yüklenemedi');
  try {
    const userId = getUserId();
    const transactionsQuery = query(
      getTransactionsCollection(),
      where('userId', '==', userId),
      where('saleDate', '>=', Timestamp.fromDate(startDate)),
      where('saleDate', '<=', Timestamp.fromDate(endDate)),
      orderBy('saleDate', 'desc')
    );

    // Try to get from cache first
    let snapshot;
    try {
      snapshot = await getDocsFromCache(transactionsQuery);
      if (snapshot.empty && snapshot.metadata.fromCache) {
        snapshot = await getDocsFromServer(transactionsQuery);
      }
    } catch (cacheError) {
      console.warn('Could not fetch transactions by date range from cache, falling back to server:', cacheError);
      snapshot = await getDocsFromServer(transactionsQuery);
    }

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        saleDate: toDateSafe(data.saleDate),
        createdAt: toDateSafe(data.createdAt),
        updatedAt: toDateSafe(data.updatedAt),
      } as SalesTransaction;
    });
  } catch (error) {
    console.error('Error fetching sales transactions by date range:', error);
    throw error;
  }
};

export const createSalesTransaction = withAuthentication(_createSalesTransaction);
export const getAllTransactions = withAuthentication(_getAllTransactions);
export const getTransactionsByDateRange = withAuthentication(_getTransactionsByDateRange); // Add this export

// Future functions like getTransactionById etc. will go here